import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, API } from "../lib/api";
import { CheckCircle2, XCircle, Download, Loader2, FileText, ShieldCheck } from "lucide-react";

const money = (n) => {
  const v = Number(n) || 0;
  return "$" + v.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

export default function QuoteAccept() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("t") || "";
  const [state, setState] = useState("loading"); // loading | ready | accepted | error
  const [quote, setQuote] = useState(null);
  const [name, setName] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/workspace/quote/${id}?t=${encodeURIComponent(token)}`);
        if (!alive) return;
        setQuote(data);
        setName(data.accepted_name || data.business_name || "");
        setState(data.status === "accepted" ? "accepted" : data.status === "declined" ? "declined" : "ready");
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.detail || "This quote link is invalid or has expired.");
        setState("error");
      }
    })();
    return () => { alive = false; };
  }, [id, token]);

  const accept = async () => {
    if (!name.trim()) { setErr("Please enter your name to accept."); return; }
    setAccepting(true); setErr("");
    try {
      const { data } = await api.post(`/workspace/quote/${id}/accept?t=${encodeURIComponent(token)}`, { name: name.trim() });
      setQuote((q) => ({ ...q, status: "accepted", accepted_at: data.accepted_at, accepted_name: data.accepted_name }));
      setState("accepted");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't record your acceptance. Please try again.");
    } finally { setAccepting(false); }
  };

  const decline = async () => {
    setDeclining(true); setErr("");
    try {
      const { data } = await api.post(`/workspace/quote/${id}/decline?t=${encodeURIComponent(token)}`, { reason: reason.trim() });
      setQuote((q) => ({ ...q, status: "declined", declined_at: data.declined_at, decline_reason: data.decline_reason }));
      setState("declined");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't record your response. Please try again.");
    } finally { setDeclining(false); }
  };

  const pdfUrl = `${API}/workspace/quote/${id}/pdf?t=${encodeURIComponent(token)}`;

  const lineItems = () => {
    if (!quote) return [];
    const items = [];
    const months = Math.round(Number(quote.months) || 1);
    items.push({ desc: `Monthly engagement — ${quote.plan_label || "Engagement"}`, detail: `${money(quote.monthly_price)}/mo × ${months} mo`, amt: money(quote.monthly_subtotal) });
    if (Number(quote.one_time_setup) > 0) items.push({ desc: "One-time setup & assessment", detail: "one-time", amt: money(quote.one_time_setup) });
    (quote.addon_lines || []).forEach((ln) => items.push({ desc: `Add-on — ${ln.name || "Add-on"}`, detail: `${money(ln.rate)} × ${ln.qty}`, amt: money(ln.total) }));
    if (Number(quote.discount) > 0) items.push({ desc: "Discount / deposit", detail: "applied", amt: "−" + money(quote.discount), neg: true });
    return items;
  };

  return (
    <div className="min-h-screen bg-[#f4f1e9] text-[#1e3d3a] flex flex-col items-center px-4 py-10 sm:py-16" data-testid="quote-accept-page">
      <div className="w-full max-w-xl">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-8 w-8 rounded-lg bg-[#1e3d3a] flex items-center justify-center text-[#c9a961] font-bold">E</div>
          <span className="text-sm font-semibold tracking-wide">ELEVARE SOLUTIONS</span>
        </div>

        {state === "loading" && (
          <div className="flex items-center gap-3 text-[#1e3d3a]/60 py-20 justify-center" data-testid="quote-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading your quote…
          </div>
        )}

        {state === "error" && (
          <div className="rounded-2xl bg-white border border-[#1e3d3a]/10 p-8 text-center" data-testid="quote-error">
            <FileText className="h-10 w-10 mx-auto text-[#1e3d3a]/30" />
            <h1 className="text-xl font-semibold mt-4">Quote unavailable</h1>
            <p className="text-[#1e3d3a]/60 mt-2 text-sm">{err}</p>
          </div>
        )}

        {(state === "ready" || state === "accepted" || state === "declined") && quote && (
          <div className="rounded-2xl bg-white border border-[#1e3d3a]/10 overflow-hidden shadow-sm">
            <div className="bg-[#1e3d3a] px-7 py-6">
              <p className="text-[#c9a961] text-[11px] tracking-[0.2em] uppercase mb-1">Proposal &amp; Quote</p>
              <h1 className="text-white text-2xl font-semibold" data-testid="quote-business">{quote.business_name || "Your quote"}</h1>
              <p className="text-[#cfd8d4] text-sm mt-1">{quote.plan_label || "Engagement"} · {quote.price_mode || "Proposed"} pricing</p>
            </div>

            <div className="px-7 py-6">
              {quote.notes && <p className="text-[#1e3d3a]/70 italic text-sm mb-4">{quote.notes}</p>}
              <table className="w-full text-sm">
                <tbody>
                  {lineItems().map((it, i) => (
                    <tr key={i} className="border-b border-[#1e3d3a]/8">
                      <td className="py-2.5 pr-3">
                        <div className="text-[#1e3d3a]">{it.desc}</div>
                        <div className="text-[#1e3d3a]/45 text-xs">{it.detail}</div>
                      </td>
                      <td className={`py-2.5 text-right whitespace-nowrap tabular-nums ${it.neg ? "text-[#b23a48]" : "text-[#1e3d3a]"}`}>{it.amt}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-between mt-4 pt-4 border-t-2 border-[#c9a961]">
                <span className="font-semibold">Total first period</span>
                <span className="text-2xl font-bold tabular-nums" data-testid="quote-total-amount">{money(quote.total)}</span>
              </div>

              <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="quote-download-pdf-link"
                className="mt-5 inline-flex items-center gap-2 text-sm text-[#1e3d3a] underline underline-offset-2 hover:text-[#c9a961]">
                <Download className="h-4 w-4" /> Download the full quote (PDF)
              </a>

              {state === "accepted" ? (
                <div className="mt-6 rounded-xl bg-[#1e3d3a]/5 border border-[#1e3d3a]/10 p-5 text-center" data-testid="quote-accepted-box">
                  <CheckCircle2 className="h-9 w-9 mx-auto text-[#2f7d5b]" />
                  <h2 className="text-lg font-semibold mt-2">Quote accepted — thank you!</h2>
                  <p className="text-[#1e3d3a]/60 text-sm mt-1">
                    Accepted by {quote.accepted_name}. We'll be in touch shortly to kick off onboarding.
                  </p>
                </div>
              ) : state === "declined" ? (
                <div className="mt-6 rounded-xl bg-[#b23a48]/5 border border-[#b23a48]/15 p-5 text-center" data-testid="quote-declined-box">
                  <XCircle className="h-9 w-9 mx-auto text-[#b23a48]" />
                  <h2 className="text-lg font-semibold mt-2">Quote declined</h2>
                  <p className="text-[#1e3d3a]/60 text-sm mt-1">
                    Thanks for letting us know{quote.decline_reason ? ":" : "."}
                    {quote.decline_reason ? <span className="block italic mt-1">“{quote.decline_reason}”</span> : null}
                  </p>
                  <p className="text-[#1e3d3a]/50 text-xs mt-2">We'll follow up if there's anything we can adjust.</p>
                </div>
              ) : (
                <div className="mt-6 rounded-xl bg-[#c9a961]/8 border border-[#c9a961]/40 p-5" data-testid="quote-accept-box">
                  <label className="block text-xs uppercase tracking-wide text-[#1e3d3a]/50 mb-1.5">Your full name</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jane Smith"
                    data-testid="quote-accept-name"
                    className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2.5 text-sm focus:border-[#c9a961] focus:outline-none mb-3" />
                  {err && <p className="text-[#b23a48] text-sm mb-3" data-testid="quote-accept-error">{err}</p>}
                  <button onClick={accept} disabled={accepting || declining} data-testid="quote-accept-button"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#c9a961] px-6 py-3 font-semibold text-[#1e3d3a] hover:bg-[#b89550] disabled:opacity-50 transition-colors">
                    {accepting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    Accept this quote
                  </button>

                  {!showDecline ? (
                    <button onClick={() => { setShowDecline(true); setErr(""); }} data-testid="quote-decline-toggle"
                      className="w-full text-center text-[13px] text-[#1e3d3a]/50 hover:text-[#b23a48] mt-3">
                      Not the right fit? Decline this quote
                    </button>
                  ) : (
                    <div className="mt-4 pt-4 border-t border-[#1e3d3a]/10" data-testid="quote-decline-form">
                      <label className="block text-xs uppercase tracking-wide text-[#1e3d3a]/50 mb-1.5">A quick reason (optional)</label>
                      <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                        placeholder="e.g. Timing isn't right / went another direction"
                        data-testid="quote-decline-reason-input"
                        className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2.5 text-sm focus:border-[#b23a48] focus:outline-none mb-3 resize-none" />
                      <div className="flex gap-2">
                        <button onClick={() => { setShowDecline(false); setReason(""); }} data-testid="quote-decline-cancel"
                          className="flex-1 rounded-full border border-[#1e3d3a]/15 px-4 py-2.5 text-sm text-[#1e3d3a]/70 hover:border-[#1e3d3a]/30">
                          Cancel
                        </button>
                        <button onClick={decline} disabled={declining || accepting} data-testid="quote-decline-button"
                          className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#b23a48] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#9c2f3c] disabled:opacity-50">
                          {declining ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                          Decline quote
                        </button>
                      </div>
                    </div>
                  )}

                  <p className="flex items-center justify-center gap-1.5 text-[11px] text-[#1e3d3a]/45 mt-3">
                    <ShieldCheck className="h-3.5 w-3.5" /> By accepting you confirm you're ready to proceed. This link is valid for 30 days.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <p className="text-center text-[#1e3d3a]/40 text-xs mt-6">Elevare Solutions · Back-office, operations &amp; growth partnership</p>
      </div>
    </div>
  );
}
